import { pantryColors } from '@pantry/ui'
import React, { useState } from 'react'
import type { LoyaltySegment } from '../../../../types/loyaltySegments'
import { formatRelativeUpdated } from '../segmentListDisplay'
import { SegmentIcon } from './segmentIcons'
import { SegmentStaleFlag, isSegmentCountStale } from './SegmentStaleNotice'

const p = pantryColors

// Parse a '#RGB' / '#RRGGBB' hex into an `rgba()` string at the given alpha —
// the reference's `--{color}-bg` tints translated to a ~12%-opacity wash of the
// segment's own color. Invalid / legacy-empty colors fall back to the brand
// primary so cards never render un-tinted.
export const segmentTint = (hex: string, alpha: number): string => {
  const raw = (hex || '').trim().replace(/^#/, '')
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return segmentTint(p.brand.primary, alpha)
  const r = Number.parseInt(full.slice(0, 2), 16)
  const g = Number.parseInt(full.slice(2, 4), 16)
  const b = Number.parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const segmentColorOrFallback = (hex: string): string => {
  const raw = (hex || '').trim()
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw) ? raw : p.brand.primary
}

// 'Updated 2h ago' style relative label from last_evaluated_at. Re-exported
// under this name (rather than importing `formatRelativeUpdated` directly)
// so the existing table/drawer imports keep working unchanged.
export const formatRelativeTime = formatRelativeUpdated

// ── Metric formatters (shared by the card and list views) ────────────────────

export const numberFormatter = new Intl.NumberFormat('en-US')

// Whole dollars, mirroring the mockup's `fmt$` ('$1,240') — the per-member
// means aren't precise enough for cents to carry meaning on a list page.
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})
export const formatSegmentCurrency = (value: number): string =>
  currencyFormatter.format(value ?? 0)

// One decimal, always ('1.0' not '1') so the column stays aligned.
const avgOrdersFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
export const formatSegmentAvgOrders = (value: number): string =>
  avgOrdersFormatter.format(value ?? 0)

interface SegmentCardProps {
  segment: LoyaltySegment
  onClick: () => void
}

// One card of the segments grid — the audience-segments mockup's `.seg-card`
// (renderCards) translated to inline styles: 4px accent bar in the segment
// colour, tinted icon square (we keep the per-segment icon_key glyph where the
// mockup uses one generic people icon — ours carries more information), name,
// description, a 3-up metrics grid (Customers/Avg LTV/Avg Orders) and the
// relative "Updated …" label. Click navigates to the segment detail page —
// per-segment actions (edit/delete) live there, not on this list card.
export const SegmentCard: React.FC<SegmentCardProps> = ({ segment, onClick }) => {
  const [hovered, setHovered] = useState(false)
  const color = segmentColorOrFallback(segment.color)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `1px solid ${p.gray[200]}`,
        borderRadius: 10,
        background: p.white,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow .15s, transform .12s',
        boxShadow: hovered
          ? '0 4px 12px rgba(0, 0, 0, 0.10)'
          : '0 1px 3px rgba(0, 0, 0, 0.08)',
        transform: hovered ? 'translateY(-1px)' : 'none',
        textAlign: 'left',
      }}
    >
      {/* .seg-card-accent */}
      <div
        style={{ height: 4, width: '100%', flexShrink: 0, background: color }}
      />

      {/* .seg-card-body */}
      <div
        style={{
          padding: '16px 18px 14px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* .seg-card-head */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: segmentTint(color, 0.12),
              color,
            }}
          >
            <SegmentIcon iconKey={segment.icon_key} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: p.gray[800],
                lineHeight: 1.2,
              }}
            >
              {segment.name}
            </div>
          </div>
        </div>

        {segment.description && (
          <div style={{ fontSize: 12, color: p.gray[500], lineHeight: 1.45 }}>
            {segment.description}
          </div>
        )}

        {/* .seg-metrics — pinned to the bottom so short descriptions don't
            leave the grid floating mid-card */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            padding: '10px 0',
            marginTop: 'auto',
            borderTop: `1px solid ${p.gray[100]}`,
            borderBottom: `1px solid ${p.gray[100]}`,
          }}
        >
          <SegmentMetric
            value={numberFormatter.format(segment.member_count ?? 0)}
            label="Customers"
            color={color}
          />
          <SegmentMetric
            value={formatSegmentCurrency(segment.avg_ltv)}
            label="Avg LTV"
          />
          <SegmentMetric
            value={formatSegmentAvgOrders(segment.avg_orders)}
            label="Avg Orders"
          />
        </div>

        {/* .seg-card-foot — the "Updated …" label is NOT proof of freshness
            when the last evaluation failed, so the warning sits right next to
            it and the metrics it qualifies. */}
        <div
          style={{
            paddingTop: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 11, color: p.gray[400] }}>
            Updated {formatRelativeTime(segment.last_evaluated_at)}
          </span>
          {isSegmentCountStale(segment) && <SegmentStaleFlag compact />}
        </div>
      </div>
    </div>
  )
}

const SegmentMetric: React.FC<{
  value: string
  label: string
  color?: string
}> = ({ value, label, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: color ?? p.gray[800] }}>
      {value}
    </div>
    <div style={{ fontSize: 10.5, color: p.gray[400] }}>{label}</div>
  </div>
)
